const OH_ACCORDION_PREFIXES = {};

/**
 * Adds basic accordion functionality to a set of elements.
 *
 * @param {Object} options
 * @param {string} options.itemSelector - querySelector string for items
 * @param {string} options.headerSelector - querySelector string for header within item
 * @param {string} options.contentSelector - querySelector string for content within item
 * @param {boolean} [options.closeAll = true] - whether all items can be closed
 * @param {boolean} [options.openFirst = false] - whether first item should start open
 */
export default function Accordion(options) {
  function generatePrefix() {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = lowercase.toUpperCase();
    const numbers = '0123456789';
    const characters = lowercase + uppercase + numbers;

    let str = 'accordion_';

    for (let i = 1; i < 20; i++) {
      str += characters[Math.floor(Math.random() * characters.length)];
    }

    return str;
  }

  let temp;

  do {
    temp = generatePrefix();
  } while (OH_ACCORDION_PREFIXES[temp]);

  const prefix = temp;

  OH_ACCORDION_PREFIXES[prefix] = 1;

  if (
    typeof options !== 'object' ||
    !options.hasOwnProperty('itemSelector') ||
    !options.hasOwnProperty('headerSelector') ||
    !options.hasOwnProperty('contentSelector')
  ) {
    console.error(
      'Accordion options need to have `itemSelector`, `headerSelector`, and `contentSelector`.'
    );

    return;
  }

  if (!options.hasOwnProperty('closeAll')) {
    options.closeAll = true;
  }

  if (!options.hasOwnProperty('openFirst')) {
    options.openFirst = false;
  }

  let items = document.querySelectorAll(options.itemSelector);

  let open = null;

  let setContentMaxHeight = function (content) {
    content.style.maxHeight = content.scrollHeight + 'px';
  };

  let toggleItem = function (itemObj) {
    if (open) {
      // itemObj is not open
      if (itemObj.item !== open.item) {
        // we will close the current open item
        closeItem(open);
      }
      // itemObj is open
      else {
        // we will close itemObj if closeAll is true
        if (options.closeAll) {
          closeItem(open);
        }

        // return early because we are dealing
        // with the currently open item
        return;
      }
    }

    // if we got here, either there wasn't an open item
    // or the toggling item was not the open item and needs to be opened
    openItem(itemObj);
  };

  let closeItem = function (itemObj) {
    itemObj.item.classList.remove('accordion-open');
    itemObj.content.style.maxHeight = null;
    itemObj.header.setAttribute('aria-expanded', false);
    itemObj.content.setAttribute('aria-hidden', true);
    itemObj.content.setAttribute('inert', '');

    open = null;
  };

  let openItem = function (itemObj) {
    itemObj.item.classList.add('accordion-open');
    setContentMaxHeight(itemObj.content);
    itemObj.header.setAttribute('aria-expanded', true);
    itemObj.content.setAttribute('aria-hidden', false);
    itemObj.content.removeAttribute('inert');

    open = itemObj;
  };

  items.forEach(function (item, i) {
    let header = item.querySelector(options.headerSelector);
    let content = item.querySelector(options.contentSelector);
    content.id = prefix + '_' + i;

    header.setAttribute('aria-expanded', false);
    header.setAttribute('aria-controls', content.id);
    content.setAttribute('aria-hidden', true);
    content.setAttribute('inert', '');

    let itemObject = {
      item: item,
      header: header,
      content: content,
    };

    header.addEventListener('click', function (e) {
      e.preventDefault();

      toggleItem(itemObject);
    });

    if (0 === i && options.openFirst) {
      toggleItem(itemObject);
    }
  });

  window.addEventListener(
    'resize',
    () => {
      if (open) {
        setContentMaxHeight(open.content);
      }
    },
    { passive: true }
  );
}
